import * as React from 'react'
import { Lock, LogOut, RotateCw } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { useBillingOwnerCandidates, useTransferBillingOwner } from '@/hooks/useOrganization'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Select } from '@/components/ui/select'
import { Card } from '@/components/ui/card'
import { BillingActions } from './BillingActions'
import { LOCK_REASON_TEXT, billingDateLabel } from '@/lib/billing'
import type { OrganizationDto } from '@lib/dto/organization.dto'

/**
 * Hand billing to another organization admin — one of the ways out of a lock,
 * for an owner who is not the right person to pay. Offered here because the
 * Users page, where it normally lives, is closed while the company is locked.
 */
function TransferBilling() {
  const candidates = useBillingOwnerCandidates(true)
  const transfer = useTransferBillingOwner()
  const [choice, setChoice] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const people = candidates.data?.candidates ?? []
  if (candidates.isPending || people.length === 0) return null

  function confirm(): void {
    setError(null)
    transfer.mutate({ userId: choice }, { onError: (e) => setError(requestErrorMessage(e)) })
  }

  return (
    <div className="flex items-end gap-2">
      <Field label="Hand billing to" htmlFor="lock-transfer" error={error} className="flex-1">
        <Select id="lock-transfer" value={choice} onChange={(e) => setChoice(e.target.value)}>
          <option value="">Choose an organization admin…</option>
          {people.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.email})
            </option>
          ))}
        </Select>
      </Field>
      <Button pending={transfer.isPending} type="button" variant="outline" onClick={confirm} disabled={choice === ''}>
        Transfer
      </Button>
    </div>
  )
}

/** What the billing owner reads: why, and what happens next. */
function OwnerBody({ organization }: { organization: OrganizationDto }) {
  const { billing } = organization
  return (
    <div className="space-y-3 text-sm text-ink-muted">
      {billing.state === 'locked' ? <p>{LOCK_REASON_TEXT[billing.lockReason]}</p> : null}
      {billing.state === 'trial_cancelled' ? (
        <p>
          The trial was canceled. This organization and everything in it will be deleted on{' '}
          {billingDateLabel(billing.deletesAt)} unless you subscribe before then.
        </p>
      ) : null}
      {billing.state === 'signup_incomplete' ? (
        <p>One more step: a card starts your free trial. You won’t be charged until it ends.</p>
      ) : (
        <p>
          Until billing is sorted out, nobody in {organization.name} — and none of its clients, share links or public
          pages — can reach anything. If someone else should take care of it, hand billing to them.
        </p>
      )}
      <BillingActions organization={organization} />
      <TransferBilling />
    </div>
  )
}

/** What everyone else in the organization reads: it is locked, and who can fix it. */
function MemberBody({ organization }: { organization: OrganizationDto | null }) {
  const owner = organization?.billingOwner ?? null
  return (
    <p className="text-sm text-ink-muted">
      {owner
        ? `Access is paused until ${owner.name} (${owner.email}), the billing owner, sorts out billing.`
        : 'Access is paused until billing is sorted out.'}
    </p>
  )
}

/**
 * The whole app, for someone whose organization billing has restricted.
 *
 * Nothing else is reachable — the API refuses it anyway — so this is not an
 * overlay on the app but a replacement for it. The billing owner is told what
 * happened and where to go; everyone else is told who to ask. A client, whose
 * venue's account this is not, is told only that it is unavailable.
 */
export function LockScreen({ organization }: { organization: OrganizationDto | null }) {
  const { user, logout } = useAuth()
  const isOwner = organization?.billingOwner?.id === user?.id
  const isClient = user?.role === 'CLIENT'

  function heading(): string {
    if (isClient) return 'Unavailable'
    const name = organization?.name ?? 'this organization'
    if (organization?.billing.state === 'signup_incomplete') return `Finish setting up ${name}`
    return `The account for ${name} is locked`
  }

  function renderBody() {
    if (isClient) {
      return (
        <div className="space-y-3">
          <p className="text-sm text-ink-muted">This venue’s account is unavailable right now.</p>
          {/* A client has no organization to watch for the lock lifting, so the
              way to find out is to ask again. */}
          <Button variant="outline" onClick={() => window.location.reload()}>
            <RotateCw className="h-4 w-4" /> Try again
          </Button>
        </div>
      )
    }
    if (isOwner && organization) return <OwnerBody organization={organization} />
    return <MemberBody organization={organization} />
  }

  return (
    <div className="flex min-h-svh items-center justify-center bg-surface p-6">
      <Card className="w-full max-w-md space-y-5 p-6">
        <div className="flex items-center gap-2">
          <Lock className="h-5 w-5 text-ink-muted" />
          <h1 className="text-lg font-semibold">{heading()}</h1>
        </div>
        {renderBody()}
        <Button variant="outline" onClick={logout}>
          <LogOut className="h-4 w-4" /> Sign out
        </Button>
      </Card>
    </div>
  )
}
